import { createSlice, type PayloadAction, type WithSlice } from '@reduxjs/toolkit';

import { rootReducer } from '@verantos-dev/app-state-base';
import {
  ResearchAssistantMessageEnumLegacy,
  type MessageLegacy,
} from '@verantos-dev/types-cohorts';

export enum ModelVersion {
  V1 = 'V1',
  V1_Llamaindex = 'V1_Llamaindex',
}

export const DEFAULT_MODEL_VERSION = ModelVersion.V1_Llamaindex;

export interface ResearchAssistantState {
  model: ModelVersion;
  messages: MessageLegacy[];
  isProcessing: boolean;
  isReady: boolean;
  transactionId: string | null;
}

const initialState: ResearchAssistantState = {
  model: DEFAULT_MODEL_VERSION,
  messages: [],
  isProcessing: false,
  isReady: false,
  transactionId: null,
};

const researchAssistantSlice = createSlice({
  name: 'researchAssistant',
  initialState,
  reducers: {
    setResearchAssistantModel: (state, action: PayloadAction<ModelVersion>) => {
      state.model = action.payload;
      state.messages = [];
    },
    addResearchAssistantMessages: (state, action: PayloadAction<MessageLegacy[]>) => {
      const newMessages = action.payload;
      const lastMessage = state.messages[state.messages.length - 1];

      // Merge info messages that are adjacent to each other
      if (lastMessage) {
        const firstNewMessage = newMessages[0];
        if (firstNewMessage && lastMessage.type === firstNewMessage.type) {
          if (
            [
              ResearchAssistantMessageEnumLegacy.INFO,
              ResearchAssistantMessageEnumLegacy.SAVE,
              ResearchAssistantMessageEnumLegacy.PROCESS,
            ].includes(lastMessage.type)
          ) {
            lastMessage.timestamp = firstNewMessage.timestamp;
            newMessages.shift();
          }
        }
      }

      // Merge adjacent info messages from the payload
      for (let i = 0; i < newMessages.length - 1; i++) {
        if (
          newMessages[i].type === ResearchAssistantMessageEnumLegacy.INFO &&
          newMessages[i + 1].type === ResearchAssistantMessageEnumLegacy.INFO
        ) {
          newMessages[i].timestamp = newMessages[i + 1].timestamp;
          newMessages.splice(i + 1, 1);
          i--;
        }
      }

      state.messages.push(...newMessages);
    },
    resetResearchAssistantMessages: (state) => {
      state.messages = [];
    },
    setResearchAssistantIsProcessing: (state, action: PayloadAction<{ transactionId: string }>) => {
      state.isProcessing = true;
      state.transactionId = action.payload.transactionId;
    },
    setResearchAssistantIsReady: (state, action: PayloadAction<boolean>) => {
      state.isReady = action.payload;
    },
    clearResearchAssistantIsProcessing: (
      state,
      action: PayloadAction<{ transactionId?: string }>
    ) => {
      if (!action.payload.transactionId || state.transactionId === action.payload.transactionId) {
        state.isProcessing = false;
        state.transactionId = null;
      }
    },
    resetResearchAssistantState: () => initialState,
  },
});

export const {
  setResearchAssistantModel,
  addResearchAssistantMessages,
  resetResearchAssistantMessages,
  setResearchAssistantIsProcessing,
  resetResearchAssistantState,
  clearResearchAssistantIsProcessing,
  setResearchAssistantIsReady,
} = researchAssistantSlice.actions;

declare module '@verantos-dev/app-state-base' {
  // WithSlice utility assumes reducer is under slice.reducerPath
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-empty-interface
  export interface LazyLoadedSlices extends WithSlice<typeof researchAssistantSlice> {}
}

// Inject the slice into the root reducer
researchAssistantSlice.injectInto(rootReducer);
